import { useState, useMemo } from "react";
import { useVerficacaoLead } from "./useVerificacaoLead";
import { useAtualizarLead } from "./useAtualizarLead";
import { buscarLeadsByIdService } from "../../../services/buscarLeadsByIdService";

// Define o tamanho exato dos pedaços
const CHUNK_SIZE = 50;

/**
 * Divide um array grande em pedaços menores (chunks) com tamanho fixo.
 *
 * @param arr - O array original que será dividido
 * @param size - O tamanho máximo de cada pedaço (chunk)
 * @returns Um array de arrays, cada um com no máximo 'size' elementos
 */

const chunkArray = (arr: any[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

export const useConverterProprietario = () => {
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { verificacao } = useVerficacaoLead();
  const { atualizar } = useAtualizarLead();

  const atualizarDataVerificado = useMemo(
    () => ({
      status: "CONVERTIDO",
      tipo: "PROPRIETARIO",
      verificado: 1,
    }),
    []
  );

  const resetError = () => setError(null);
  const resetSuccess = () => setSuccess(null);
  const resetWarning = () => setWarning(null);

  const converterProprietario = async (leads: any[]) => {
    if (!leads.length) {
      setWarning("Nenhum Lead foi selecionado.");
      return;
    }

    setIsLoading(true);
    try {
      const updatedLeads = [];
      let leadsConvertidos = 0;
      let leadsJaConvertidos = 0;
      let celularesNaoVerificados = 0;

      const chunks = chunkArray(leads, CHUNK_SIZE);

      for (const chunk of chunks) {
        const results = await Promise.all(
          chunk.map(async (item) => {
            try {
              const lead = await buscarLeadsByIdService(item);
              const isCelular = await verificacao(lead.celular);
              const isVerificado = lead.verificado === 1;

              if (isVerificado) {
                leadsJaConvertidos++;
                return item;
              }

              if (isCelular) {
                await atualizar(item, atualizarDataVerificado);
                leadsConvertidos++;
                return { ...item, verificado: true };
              } else {
                celularesNaoVerificados++;
                return item;
              }
            } catch (err) {
              console.error("Erro individual no lead:", item, err);
              return null;
            }
          })
        );

        updatedLeads.push(...results.filter(Boolean));
      }

      if (leadsConvertidos > 0) {
        setSuccess(
          `${leadsConvertidos} lead(s) selecionados convertido(s) com sucesso!`
        );
      }

      if (leadsJaConvertidos > 0) {
        setWarning(
          `${leadsJaConvertidos} lead(s) selecionados já  foram convertido(s).`
        );
      }

      if (celularesNaoVerificados > 0) {
        setWarning((prev) =>
          prev
            ? `${prev} | ${celularesNaoVerificados} não tiveram o celular verificado.`
            : `${celularesNaoVerificados} não tiveram o celular verificado.`
        );
      }

      if (
        leadsConvertidos === 0 &&
        leadsJaConvertidos === 0 &&
        celularesNaoVerificados === 0
      ) {
        setWarning("Nenhum proprietário foi convertido.");
      }
    } catch (err) {
      console.error("Erro geral:", err);
      setError("Erro ao converter proprietários. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    converterProprietario,
    isLoading,
    success,
    resetSuccess,
    error,
    resetError,
    warning,
    resetWarning,
  };
};
